import { useTranslator } from '@bluebeam/react-i18n';
import Tabs from 'components/Tabs';
import Tab from 'components/Tabs/Tab';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';
import React, { useState } from 'react';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ModalStdHeader from '../ModalStdHeader';
import ExportTab from './ExportTab';
import UploadTab from './UploadTab';

type TabType = 'Uploads' | 'Exports';

const FileManagerModalFC: React.FC<WithAppStoreProps> = ({
  actions,
  appStore,
}) => {
  const { translate } = useTranslator(['buttons', 'fileManagerModalFC']);
  const [selectedTab, setSelectedTab] = useState<TabType>('Uploads');
  const { state } = appStore;
  const {
    recentUploads,
    activeUploads,
    activeExports,
    processingStatuses,
    selectedProject,
  } = state;

  const { areIntegrationsEnabled } = useIntegrationsCheck();

  const handleCancelClick = (): void => {
    const { clearAllModals } = actions;
    clearAllModals();
  };

  const handleTabChange = (tab: string): void => {
    setSelectedTab(tab as TabType);
  };

  const handleClearErrorsClick = async (): Promise<void> => {
    switch (selectedTab) {
      case 'Uploads':
        await actions.clearRecentDocumentErrors(
          Array.from(recentUploads.values())
            .flatMap((ru) => ru.documentsProgress)
            .filter((du) => du.error && du.error.length > 0)
            .map((d) => d.id)
        );
        await actions.getRecentUploads();
        break;
      default:
        break;
    }
  };

  const nonUploadingProcessingRequestIds = Array.from(
    processingStatuses.values()
  )
    .filter((p) => p.status !== 'Uploading')
    .map((p) => p.processingRequestid);

  const uploadingProcessingRequestIds = Array.from(processingStatuses.values())
    .filter((p) => p.status === 'Uploading')
    .map((p) => p.processingRequestid);

  const activeUploadsValues = Array.from(activeUploads.values()).filter(
    (auv) => !nonUploadingProcessingRequestIds.includes(auv.processingRequestId)
  );

  const recentUploadValues = Array.from(recentUploads.values()).filter(
    (ruv) => !uploadingProcessingRequestIds.includes(ruv.processingRequestId)
  );

  const allUploads = activeUploadsValues
    .flatMap((au) => au.documentsProgress)
    .concat(recentUploadValues.flatMap((ru) => ru.documentsProgress))
    .sort((a, b) => {
      if (a.error !== '' && b.error === '') return -1;
      if (a.error === '' && b.error !== '') return 1;
      if (a.timestamp < b.timestamp) return -1;
      if (a.timestamp > b.timestamp) return 1;
      return 0;
    });

  const uploadErrorCount = allUploads.filter(
    (u) => u.error && u.error !== ''
  ).length;

  return (
    <div id="file-upload-modal" className="modal__content w100">
      <ModalStdHeader text={translate('fileManagerModalFC:fileManager')} />
      <div className="h100 w100 pt9 pb5" style={{ maxHeight: '586px' }}>
        <div className="h100 w100">
          <div className="mx4">
            <Tabs
              tabNames={['Uploads', 'Exports']}
              onChange={handleTabChange}
              selectedTab={selectedTab}
            >
              <Tab
                name="Uploads"
                placeholder={translate('fileManagerModalFC:uploads')}
                activeCount={allUploads.length}
                errorCount={uploadErrorCount}
              />
              <Tab
                name="Exports"
                placeholder={translate('fileManagerModalFC:exports')}
                activeCount={activeExports.size}
              />

              {/* <LinkTab name="Requests" path={`${match.url}/requests`} /> */}
            </Tabs>
          </div>
          <div className="column h100 w100">
            {(() => {
              if (selectedTab === 'Uploads') return <UploadTab />;
              if (selectedTab === 'Exports') return <ExportTab />;
              return null;
            })()}
          </div>
        </div>
      </div>
      <div
        className="bt-2 row-reverse"
        style={{ height: '72px', alignItems: 'center' }}
      >
        <button
          id="cancel-btn"
          className="btn btn-primary mr5"
          onClick={handleCancelClick}
        >
          {translate('buttons:close')}
        </button>
        <div style={{ flex: '1 1' }} />
        {selectedTab === 'Uploads' && uploadErrorCount > 0 && (
          <button
            id="cancel-btn"
            className="btn btn-link ml5"
            onClick={handleClearErrorsClick}
          >
            {translate('buttons:clearErrors')}
          </button>
        )}
      </div>
    </div>
  );
};

export default withAppStore(FileManagerModalFC);
